import { createContext, useContext, createSignal, ParentComponent, onMount } from 'solid-js';
import { useUserStatus } from '../hooks/useUserStatus';

interface User {
	userId: string;
	name: string;
	bankroll: number;
	activeGameIds: string[];
	matchmakingQueueId: string | null;
}

interface AuthContextType {
	user: () => User | null;
	setUser: (user: User) => void;
	logout: () => void;
	isLoading: () => boolean;
	authError: () => boolean;
	hasActiveGames: () => boolean;
	hasMatchmakingQueue: () => boolean;
	clearRecoveryState: () => void;
}

const AuthContext = createContext<AuthContextType>();

export const AuthProvider: ParentComponent = (props) => {
	const [user, setUserInternal] = createSignal<User | null>(null);
	const [isLoading, setIsLoading] = createSignal(true);
	const [authError, setAuthError] = createSignal(false);
	const { fetchUserStatus, state } = useUserStatus();

	const setUser = (newUser: User) => {
		// Clean up invalid session data before setting user
		const cleanedUser = cleanupUserSessions(newUser);
		setUserInternal(cleanedUser);
		setAuthError(false); // Clear any previous auth errors
		// Store user info in localStorage
		localStorage.setItem(
			'auth',
			JSON.stringify({ isAuthenticated: true, username: cleanedUser.name, userId: cleanedUser.userId }),
		);
	};

	// Helper function to clean up invalid session data
	const cleanupUserSessions = (userData: User): User => {
		const validGameIds: string[] = [];
		let hasChanges = false;

		// Filter out invalid game IDs
		userData.activeGameIds.forEach((gameId) => {
			if (gameId && typeof gameId === 'string' && gameId.trim().length > 0) {
				validGameIds.push(gameId);
			} else {
				hasChanges = true;
			}
		});

		// Validate queue ID
		let validQueueId = userData.matchmakingQueueId;
		if (
			userData.matchmakingQueueId &&
			(typeof userData.matchmakingQueueId !== 'string' || userData.matchmakingQueueId.trim().length === 0)
		) {
			validQueueId = null;
			hasChanges = true;
		}

		// Return cleaned data if changes were made, otherwise return original
		if (hasChanges) {
			return {
				...userData,
				activeGameIds: validGameIds,
				matchmakingQueueId: validQueueId,
			};
		}

		return userData;
	};

	const clearAuth = (isError = false) => {
		setUserInternal(null);
		localStorage.removeItem('auth');
		setAuthError(isError); // Track if this was due to an auth error
		setIsLoading(false); // Ensure loading is false after clearing
	};

	// Check for existing authentication on mount
	onMount(async () => {
		// If user is already authenticated, ensure it's persisted to localStorage and skip fetching
		const currentUser = user();
		if (currentUser) {
			localStorage.setItem('auth', JSON.stringify({ isAuthenticated: true, username: currentUser.name }));
			setIsLoading(false);
			return;
		}

		// Check localStorage for cached auth state for UX continuity
		const cachedAuth = localStorage.getItem('auth');
		if (cachedAuth) {
			try {
				const { isAuthenticated, username } = JSON.parse(cachedAuth);
				if (isAuthenticated && username) {
					// Fetch fresh user data from server using the JWT cookie
					const userStatus = await fetchUserStatus();

					if (userStatus) {
						// Server validated our JWT and returned user data
						setUserInternal({
							userId: userStatus.userId,
							name: userStatus.displayName,
							bankroll: userStatus.totalBalance,
							activeGameIds: userStatus.activeGameIds,
							matchmakingQueueId: userStatus.matchmakingQueueId,
						});
					} else {
						// Check if it was a 401 (auth rejection) or other error
						const { error } = state();
						if (error === 'Not authenticated') {
							// Server rejected our JWT (expired/invalid), clear cached auth
							localStorage.removeItem('auth');
							setAuthError(true);
						} else {
							// Network or server error - don't clear auth, just show error
							setAuthError(true);
						}
					}

					setIsLoading(false);
					return;
				}
			} catch (error) {
				console.error('Failed to parse cached auth or fetch user status:', error);
				localStorage.removeItem('auth');
				setAuthError(true);
			}
		}

		setIsLoading(false);
	});

	// Recovery functions
	const hasActiveGames = () => {
		const currentUser = user();
		return currentUser ? currentUser.activeGameIds.length > 0 : false;
	};

	const hasMatchmakingQueue = () => {
		const currentUser = user();
		return currentUser ? currentUser.matchmakingQueueId !== null : false;
	};

	const clearRecoveryState = () => {
		const currentUser = user();
		if (currentUser) {
			setUserInternal({
				...currentUser,
				activeGameIds: [],
				matchmakingQueueId: null,
			});
		}
	};

	const contextValue: AuthContextType = {
		user,
		setUser,
		logout: clearAuth,
		isLoading,
		authError,
		hasActiveGames,
		hasMatchmakingQueue,
		clearRecoveryState,
	};

	return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;
};

export const useAuthenticatedUser = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuthenticatedUser must be used within an AuthProvider');
	}
	return context;
};
